import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, Users, Phone, Mail, MessageSquare, Check, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReservationsPanel = () => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, today, upcoming
  const [now, setNow] = useState(new Date());

  const fetchReservations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/reservations/display`);
      setReservations(res.data || []);
    } catch (error) {
      console.error('Error fetching reservations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReservations();
    const interval = setInterval(() => {
      fetchReservations();
      setNow(new Date());
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchReservations]);

  const today = now.toISOString().split('T')[0];
  const tomorrow = new Date(now.getTime() + 86400000).toISOString().split('T')[0];

  const filtered = reservations.filter(r => {
    if (filter === 'today') return r.date === today;
    if (filter === 'upcoming') return r.date > today;
    return true;
  });

  const todayCount = reservations.filter(r => r.date === today).length;
  const tomorrowCount = reservations.filter(r => r.date === tomorrow).length;
  const pendingCount = reservations.filter(r => r.status === 'pending').length;
  const totalGuests = filtered.reduce((sum, r) => sum + r.guests, 0);

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    const months = ['jan', 'fév', 'mar', 'avr', 'mai', 'jun', 'jul', 'aoû', 'sep', 'oct', 'nov', 'déc'];
    return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}`;
  };

  const isToday = (dateStr) => dateStr === today;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#d4af37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] p-4 md:p-6">
      {/* Header */}
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl md:text-2xl text-white font-semibold tracking-wide">BÉNI — Réservations</h1>
            <p className="text-white/40 text-xs mt-1">
              Mis à jour : {now.toLocaleTimeString('fr-FR', {hour:'2-digit', minute:'2-digit'})}
            </p>
          </div>
          <button onClick={fetchReservations} className="text-[#d4af37] text-sm border border-[#d4af37]/30 px-3 py-1.5 rounded hover:bg-[#d4af37]/10 transition">
            Actualiser
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-[#121212] border border-white/10 p-3 rounded-lg text-center">
            <p className="text-2xl text-white font-bold">{todayCount}</p>
            <p className="text-white/50 text-xs">Aujourd'hui</p>
          </div>
          <div className="bg-[#121212] border border-white/10 p-3 rounded-lg text-center">
            <p className="text-2xl text-white font-bold">{tomorrowCount}</p>
            <p className="text-white/50 text-xs">Demain</p>
          </div>
          <div className="bg-[#121212] border border-white/10 p-3 rounded-lg text-center">
            <p className="text-2xl text-[#d4af37] font-bold">{pendingCount}</p>
            <p className="text-white/50 text-xs">En attente</p>
          </div>
          <div className="bg-[#121212] border border-white/10 p-3 rounded-lg text-center">
            <p className="text-2xl text-white font-bold">{totalGuests}</p>
            <p className="text-white/50 text-xs">Couverts ({filter === 'today' ? "auj." : "total"})</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-4">
          {[
            { id: 'today', label: "Aujourd'hui" },
            { id: 'upcoming', label: 'À venir' },
            { id: 'all', label: 'Tout' }
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 text-sm rounded transition ${
                filter === f.id
                  ? 'bg-[#d4af37] text-black font-medium'
                  : 'bg-white/5 text-white/60 hover:bg-white/10'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Reservations List */}
        <div className="space-y-2">
          <AnimatePresence>
            {filtered.length === 0 ? (
              <div className="text-center py-16">
                <Calendar className="w-10 h-10 text-white/20 mx-auto mb-3" />
                <p className="text-white/40">Aucune réservation</p>
              </div>
            ) : (
              filtered.map((r, idx) => (
                <motion.div
                  key={r.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.03 }}
                  className={`bg-[#121212] border rounded-lg p-3 md:p-4 flex items-center gap-3 md:gap-4 ${
                    r.status === 'confirmed' ? 'border-green-500/30' :
                    r.status === 'cancelled' ? 'border-red-500/20 opacity-50' :
                    'border-[#d4af37]/30'
                  }`}
                >
                  {/* Status indicator */}
                  <div className={`w-2 h-2 rounded-full shrink-0 ${
                    r.status === 'confirmed' ? 'bg-green-500' :
                    r.status === 'cancelled' ? 'bg-red-500' :
                    'bg-[#d4af37] animate-pulse'
                  }`} />

                  {/* Date/Time */}
                  <div className="shrink-0 w-16 md:w-20 text-center">
                    <p className={`text-xs font-medium ${isToday(r.date) ? 'text-[#d4af37]' : 'text-white/60'}`}>
                      {isToday(r.date) ? "Auj." : formatDate(r.date)}
                    </p>
                    <p className="text-white text-sm font-bold">{r.time}</p>
                  </div>

                  {/* Guest info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm truncate">{r.name}</p>
                    <div className="flex items-center gap-3 text-white/40 text-xs mt-0.5">
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" />{r.guests}</span>
                      <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{r.phone}</span>
                    </div>
                  </div>

                  {/* Notes */}
                  {r.notes && (
                    <div className="hidden md:flex items-center gap-1 text-white/30 text-xs max-w-[200px]">
                      <MessageSquare className="w-3 h-3 shrink-0" />
                      <span className="truncate">{r.notes}</span>
                    </div>
                  )}

                  {/* Status badge */}
                  <div className={`shrink-0 px-2 py-0.5 rounded text-xs font-medium ${
                    r.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                    r.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                    'bg-[#d4af37]/20 text-[#d4af37]'
                  }`}>
                    {r.status === 'confirmed' ? 'Confirmé' : r.status === 'cancelled' ? 'Annulé' : 'En attente'}
                  </div>
                </motion.div>
              ))
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default ReservationsPanel;
